/**
 * The app-wide edit lock (decision 28; sync case table, L1 and "active tab"; part 06 log, tables E and S). One tab
 * per device holds a Web Lock and alone writes the local store. Every acquire or steal bumps the lock epoch, and
 * every write carries it, so a tab that lost the lock can't commit. "Use here" asks the holder to save and let go,
 * and steals the lock after 2 s from a holder that doesn't answer (a suspended background tab). A tab that finds
 * it lost the lock stops editing, and typing it couldn't save goes into a new conflict copy.
 *
 * Web Locks decides who holds the lock and IndexedDB orders the bumps, so a tab only trusts its epoch after its
 * bump committed and the lock manager, asked afterwards, showed it holding the lock (principle 1). A tab learns
 * its own client ID from a lock of its own that nobody else can name.
 */
import type { ChapterText, ConflictCopy } from './records.ts';
import type { Store, StoreFailure, StoreOp } from './store.types.ts';

/** The name of the app-wide edit lock. */
export const EDIT_LOCK = 'cockatrice:edit';

/** The prefix of each tab's own lock, which tells the tab its client ID in a lock query. */
export const TAB_LOCK_PREFIX = 'cockatrice:tab:';

/** How long "use here" waits for the holder to let go before stealing (L1). */
export const USE_HERE_WAIT_MS = 2000;

/** How often one write may bump again after meeting a newer epoch while holding the lock (E11). */
export const MAX_RECOVERIES = 8;

/** How many fresh IDs a conflict copy tries before giving up (W8). */
export const MAX_COPY_TRIES = 8;

/** The part of the Web Locks API the session uses; `navigator.locks` is one. */
export interface Locks {
  request(name: string, options: LockOptions, callback: (lock: Lock | null) => Promise<void> | undefined): Promise<unknown>;
  query(): Promise<LockManagerSnapshot>;
}

/** The part of a `BroadcastChannel` the session uses. */
export interface Channel {
  postMessage(message: unknown): void;
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  close(): void;
}

/**
 * Where a session stands. `starting`: taking the lock. `active`: holds it and writes. `releasing`: saving before
 * letting another tab have it. `elsewhere`: another tab edits. `stale`: lost the lock after editing. `failed`:
 * couldn't take the lock (E10, E16). `outdated`: a later build upgraded the store (V3). `disconnected`: the browser
 * closed the store's connection (V6); the app opens a new store and session and saves the editor's typing through
 * them. `closed`: the tab closed.
 */
export type SessionState =
  | 'starting'
  | 'active'
  | 'releasing'
  | 'elsewhere'
  | 'stale'
  | 'failed'
  | 'outdated'
  | 'disconnected'
  | 'closed';

/** The outcome of {@link EditSession.write}: committed, not written because the lock is gone, or failed (W3). */
export type SessionWriteResult = { readonly kind: 'committed' } | { readonly kind: 'stale' } | StoreFailure;

/** The outcome of {@link EditSession.save}: saved as the chapter, kept as a new conflict copy, or failed. */
export type SaveResult = { readonly kind: 'saved' } | { readonly kind: 'copied'; readonly copyId: string } | StoreFailure;

/** What a session needs; everything with an effect is injected. */
export interface EditSessionOptions {
  readonly store: Store;
  /** `navigator.locks`; undefined where Web Locks is missing (E16). */
  readonly locks: Locks | undefined;
  /** A `BroadcastChannel` every tab of the app opens under one name. */
  readonly channel: Channel;
  /**
   * The edit lock's name: {@link EDIT_LOCK} unless the store is one of several (part 36a, table BK: an account's own
   * store), each with a lock of its own, so tabs on different stores never take each other's.
   */
  readonly editLock?: string;
  /** Resolves after `ms` milliseconds. */
  readonly delay: (ms: number) => Promise<void>;
  /** A fresh ID (32 lowercase hex digits, 128 random bits). */
  readonly newId: () => string;
  /** Milliseconds since the Unix epoch. */
  readonly now: () => number;
  /**
   * Saves the editor's unsaved typing through {@link EditSession.save}. The session calls it before letting
   * another tab have the lock, when it finds it lost the lock, on a version change and on close. It should save
   * only typing no save in flight already covers.
   */
  readonly saveUnsaved: () => Promise<void>;
  /** Called on every state change. */
  readonly onState?: (state: SessionState) => void;
}

/** Thrown into results when saving through a closed session (S8). */
export class SessionClosedError extends Error {
  override readonly name = 'SessionClosedError';

  constructor() {
    super('the edit session is closed');
  }
}

/** Thrown into the `failed` state when the browser has no Web Locks (E16). */
export class LocksUnsupportedError extends Error {
  override readonly name = 'LocksUnsupportedError';

  constructor() {
    super('this browser has no Web Locks');
  }
}

/** The message "use here" sends the holder. */
interface ReleaseRequest {
  readonly kind: 'cockatrice:release';
  readonly from: string;
}

/** Whether `data` is a release request (E15): anything else on the channel is ignored. */
export function isReleaseRequest(data: unknown): boolean {
  return typeof data === 'object' && data !== null && Reflect.get(data, 'kind') === 'cockatrice:release';
}

/** A lock the tab holds, and how to let it go. */
interface Held {
  readonly release: () => void;
}

const nothing = (): void => undefined;

/** No lock: letting it go does nothing. */
const NOT_HELD: Held = { release: nothing };

/** The lock manager of a browser without Web Locks (E16): every call fails, so the session ends `failed`. */
const unsupported = (): Promise<never> => Promise.reject(new LocksUnsupportedError());
const NO_LOCKS: Locks = { request: unsupported, query: unsupported };

/** A tab's hold on the edit lock and its epoch (part 06 log, table E). */
export class EditSession {
  private current: SessionState = 'starting';
  private epoch: number | null = null;
  /**
   * The current turn: a fresh object for each start or "use here", replaced whenever the lock is let go, so a late
   * event from an earlier turn changes nothing.
   */
  private turn: object = {};
  private edit: Held = NOT_HELD;
  private tab: Held | null = null;
  private pending: Promise<unknown> | null = null;
  private recovering: Promise<number | null> | null = null;
  private started = false;
  private readonly tabLock: string;
  private readonly editLock: string;
  private readonly locks: Locks;
  private readonly options: EditSessionOptions;

  constructor(options: EditSessionOptions) {
    this.options = options;
    this.locks = options.locks ?? NO_LOCKS;
    this.tabLock = `${TAB_LOCK_PREFIX}${options.newId()}`;
    this.editLock = options.editLock ?? EDIT_LOCK;
    options.channel.addEventListener('message', (event) => {
      this.onMessage(event.data);
    });
    options.store.onVersionChange(() => this.onVersionChange());
    options.store.onClose(() => {
      this.onStoreClosed();
    });
  }

  /** Why the session is `failed`, for the app to show; undefined otherwise. */
  failure: unknown = undefined;

  /** The current state. */
  get state(): SessionState {
    return this.current;
  }

  /**
   * Takes the lock if it is free (E1), or settles `elsewhere` (E2). Resolves with the state it reached. Runs once;
   * later calls resolve with the current state.
   */
  start(): Promise<SessionState> {
    if (this.started) return Promise.resolve(this.current);
    this.started = true;
    return this.track(async () => {
      const turn = this.begin();
      try {
        await this.holdTabLock();
        const edit = await this.hold(this.editLock, { ifAvailable: true }, () => {
          this.lost(turn);
        });
        await this.take(turn, edit);
      } catch (error) {
        this.fail(turn, error);
      }
    });
  }

  /**
   * "Use here" (L1): asks the holder to save and let go, waits up to {@link USE_HERE_WAIT_MS}, then steals (E3–E7).
   * Does nothing while the session is `starting`, `active`, `releasing`, `outdated` or `closed`.
   */
  useHere(): Promise<SessionState> {
    const from = this.current;
    if (from !== 'elsewhere' && from !== 'stale' && from !== 'failed') return Promise.resolve(from);
    return this.track(async () => {
      const turn = this.begin();
      this.setState('starting');
      const message: ReleaseRequest = { kind: 'cockatrice:release', from: this.tabLock };
      this.options.channel.postMessage(message);
      const onLost = (): void => {
        this.lost(turn);
      };
      try {
        await this.holdTabLock();
        const timeout = new AbortController();
        void this.options.delay(USE_HERE_WAIT_MS).then(() => {
          timeout.abort();
        });
        const waited = await this.hold(this.editLock, { signal: timeout.signal }, onLost).catch((error: unknown) => {
          if (timeout.signal.aborted) return null;
          throw error;
        });
        // A session that closed, lost its store or met a later build during the wait never steals (E19).
        if (waited === null && turn !== this.turn) return;
        await this.take(turn, waited ?? (await this.hold(this.editLock, { steal: true }, onLost)));
      } catch (error) {
        this.fail(turn, error);
      }
    });
  }

  /**
   * Checks with the lock manager that this tab still holds the lock, for a tab coming back from the background
   * (E12, E13). A tab that lost it turns `stale`.
   */
  async check(): Promise<SessionState> {
    if (this.current !== 'active') return this.current;
    const turn = this.turn;
    if (!(await this.holds())) this.lost(turn);
    return this.current;
  }

  /**
   * Writes `ops` atomically under this tab's epoch (table W). A write that meets a newer epoch while the lock
   * manager still shows this tab holding the lock bumps again and retries (E8, E11); otherwise the tab turns
   * `stale` and nothing is written. Waits for a start or "use here" in progress (S7). A write is of the turn it was
   * issued in: one that comes back stale after that turn ended is `stale`, even when a new turn holds the lock, so it
   * never lands under the new turn after it loaded the chapter (W-d, part 24a).
   */
  async write(ops: readonly StoreOp[]): Promise<SessionWriteResult> {
    return this.writeIn(null, ops);
  }

  /**
   * Part 24c, principle 6: a write bound to the turn this is called in. Each of its calls writes as {@link write} does,
   * but only in that turn: once the tab let go of the lock, even if it took it again since, every call is `stale`.
   */
  writer(): (ops: readonly StoreOp[]) => Promise<SessionWriteResult> {
    const turn = this.turn;
    return (ops) => this.writeIn(turn, ops);
  }

  /** {@link write} in `bound`, or in the turn current once a start or "use here" in progress is done (null). */
  private async writeIn(bound: object | null, ops: readonly StoreOp[]): Promise<SessionWriteResult> {
    await this.pending;
    if (this.current === 'closed') return { kind: 'failed', error: new SessionClosedError() };
    const turn = bound ?? this.turn;
    for (let recoveries = 0; ; recoveries++) {
      const epoch = this.epoch;
      if (epoch === null || turn !== this.turn) return { kind: 'stale' };
      const result = await this.options.store.write(epoch, ops);
      if (result.kind !== 'stale') return result;
      // Another write of this turn already recovered from this epoch: retry under the new one (W-b).
      if (this.epoch !== epoch) continue;
      if (recoveries >= MAX_RECOVERIES || (await this.recover(turn)) === null) {
        this.lost(turn);
        return { kind: 'stale' };
      }
    }
  }

  /**
   * Saves a chapter's text (table S): as the chapter while this tab holds the lock (S1, S2), otherwise as a new
   * conflict copy (S3, S4). Never rejects: a failure comes back as `failed`, and the caller keeps the text (S5,
   * S6, S8).
   */
  async save(chapter: ChapterText): Promise<SaveResult> {
    try {
      const result = await this.write([{ kind: 'put', store: 'chapters', value: chapter }]);
      if (result.kind === 'committed') return { kind: 'saved' };
      if (result.kind === 'failed') return result;
      return await this.copy(chapter);
    } catch (error) {
      return { kind: 'failed', error };
    }
  }

  /**
   * For the page going away (`pagehide`): saves unsaved typing, then lets go of both locks and the channel (E17).
   * The editor takes no typing once it is called; later saves fail (S8). Closing is final: a page restored from the
   * back-forward cache opens a new session on `pageshow` (E22).
   */
  async close(): Promise<void> {
    if (this.current === 'closed') return;
    await this.options.saveUnsaved().catch(() => undefined);
    this.letGo();
    this.tab?.release();
    this.options.channel.close();
    this.setState('closed');
  }

  /**
   * Runs `body` as the start or "use here" in progress, which writes wait for, and resolves with the state. The
   * wait is in place before `body` runs, so a save from a state callback it triggers waits too. Once it ends,
   * waiting on it costs a write nothing, so it stays until the next one replaces it.
   */
  private async track(body: () => Promise<void>): Promise<SessionState> {
    let done = nothing;
    this.pending = new Promise<void>((resolve) => {
      done = resolve;
    });
    await body();
    done();
    return this.current;
  }

  /** Starts a new turn and returns it. */
  private begin(): object {
    const turn = {};
    this.turn = turn;
    return turn;
  }

  /** Ends the turn: no epoch, and the edit lock let go. */
  private letGo(): void {
    this.turn = {};
    this.epoch = null;
    this.edit.release();
  }

  /** Takes this tab's own lock once, which names its client ID in a lock query. */
  private async holdTabLock(): Promise<void> {
    if (this.tab !== null) return;
    // Nobody else can name the tab lock, so nobody can steal it.
    const tab = await this.hold(this.tabLock, { ifAvailable: true }, nothing);
    if (tab === null) throw new Error('the tab lock is taken');
    // Granted after the session closed: let it go at once (E21).
    if (this.current === 'closed') tab.release();
    else this.tab = tab;
  }

  /**
   * Requests `name` and resolves once the lock manager answers: with a handle while the lock is held, or null
   * when `ifAvailable` found it taken. `onLost` runs if the lock is later taken away (a steal).
   */
  private hold(name: string, options: LockOptions, onLost: () => void): Promise<Held | null> {
    return new Promise((resolve, reject) => {
      let granted = false;
      this.locks
        .request(name, options, (lock) => {
          if (lock === null) {
            resolve(null);
            return undefined;
          }
          granted = true;
          return new Promise<void>((release) => {
            resolve({ release });
          });
        })
        .catch((error: unknown) => {
          if (granted) onLost();
          // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- the lock manager's own error, passed through unchanged
          else reject(error);
        });
    });
  }

  /**
   * With the lock (`edit`): bumps the epoch and confirms (principle 1), then `active`, or `elsewhere` if the lock
   * was taken meanwhile (E8). Without it (`ifAvailable` found it taken): `elsewhere` (E2).
   */
  private async take(turn: object, edit: Held | null): Promise<void> {
    if (edit === null) {
      if (turn === this.turn) this.setState('elsewhere');
      return;
    }
    // A grant for a turn that already ended is let go untouched (E18, E20).
    if (turn !== this.turn) {
      edit.release();
      return;
    }
    this.edit = edit;
    const epoch = await this.options.store.bumpEpoch();
    const holds = await this.holds();
    if (turn !== this.turn) return;
    if (!holds) {
      edit.release();
      this.setState('elsewhere');
      return;
    }
    this.epoch = epoch;
    this.failure = undefined;
    this.setState('active');
  }

  /** Whether the lock manager shows this tab holding the edit lock. */
  private async holds(): Promise<boolean> {
    const held = (await this.locks.query()).held;
    if (held === undefined) return false;
    const me = held.find((lock) => lock.name === this.tabLock)?.clientId;
    return me !== undefined && held.some((lock) => lock.name === this.editLock && lock.clientId === me);
  }

  /**
   * After a write met a newer epoch: if this tab still holds the lock, bumps and confirms again and takes the new
   * epoch (E8, E11); null otherwise. One recovery at a time; writes that meet the same newer epoch share it.
   */
  private recover(turn: object): Promise<number | null> {
    this.recovering ??= (async () => {
      try {
        if (!(await this.holds())) return null;
        const epoch = await this.options.store.bumpEpoch();
        if (!(await this.holds()) || turn !== this.turn) return null;
        this.epoch = epoch;
        return epoch;
      } catch {
        return null;
      } finally {
        this.recovering = null;
      }
    })();
    return this.recovering;
  }

  /** The lock of `turn` is gone: a tab that was editing turns `stale` and saves its typing as copies (E9). */
  private lost(turn: object): void {
    if (turn !== this.turn) return;
    if (this.current !== 'active' && this.current !== 'releasing') return;
    this.letGo();
    this.setState('stale');
    void this.options.saveUnsaved().catch(() => undefined);
  }

  /** `turn` couldn't take the lock (E10, E16): lets go of it, nothing is written. */
  private fail(turn: object, error: unknown): void {
    if (turn !== this.turn) return;
    this.letGo();
    this.failure = error;
    this.setState('failed');
  }

  private onMessage(data: unknown): void {
    if (!isReleaseRequest(data) || this.current !== 'active') return;
    const turn = this.turn;
    this.setState('releasing');
    void this.options
      .saveUnsaved()
      .catch(() => undefined)
      .then(() => {
        if (turn !== this.turn) return;
        this.letGo();
        this.setState('elsewhere');
      });
  }

  /** A later build is upgrading the store (V3): saves as things stand, then lets go; the connection closes after. */
  private async onVersionChange(): Promise<void> {
    if (this.current === 'closed') return;
    await this.options.saveUnsaved().catch(() => undefined);
    this.letGo();
    this.setState('outdated');
  }

  /**
   * The browser closed the store's connection (V6): nothing can be written through it again, so the tab lets go of
   * the lock (another tab, or a new session in this one, can take it) and says so. The typing it holds is the
   * editor's to save through the new session.
   */
  private onStoreClosed(): void {
    if (this.current === 'closed' || this.current === 'outdated') return;
    this.letGo();
    this.setState('disconnected');
  }

  /** Keeps `chapter` as a new conflict copy under a fresh ID (S3, S4, W7, W8). */
  private async copy(chapter: ChapterText): Promise<SaveResult> {
    for (let tries = 0; tries < MAX_COPY_TRIES; tries++) {
      const copy: ConflictCopy = {
        id: this.options.newId(),
        chapterId: chapter.id,
        bookId: chapter.bookId,
        text: chapter.text,
        createdAt: this.options.now(),
        origin: 'stale-tab',
      };
      const result = await this.options.store.create('conflictCopies', copy);
      if (result.kind === 'created') return { kind: 'copied', copyId: copy.id };
      if (result.kind === 'failed') return result;
    }
    return { kind: 'failed', error: new Error('no free ID for a conflict copy') };
  }

  private setState(state: SessionState): void {
    this.current = state;
    this.options.onState?.(state);
  }
}
